import { authoritativeThreadTitle } from '../services/threadMetadata/titleHistory';
import { ChatThread, THREAD_TITLE_HISTORY_MAX, ThreadTitleHistoryEntry } from '../components/GlobalChatSystem/types';
import { ts } from '../repositories/firestoreUtils';

export interface ChatThreadDoc {
  id: string;
  instanceId: string;
  title: string;
  createdAt: string;
  lastActiveAt: string;
  lastViewedAt: string;
  unreadCount: number;
  isProcessing: boolean;
  messageCount: number;
  topicSummary?: string;
  userRenamed?: boolean;
  /** T1 (thread-workflow UX): user pin state — pinned threads order first. */
  pinned?: boolean;
  pinnedAt?: string;
  /**
   * USER-CONTENT SEMANTICS (personalized greeting, 2026-07-13): true when the
   * transcript holds at least one USER message. Greeting-seeded threads carry
   * messageCount > 0 with hasUserContent:false and must still count as empty
   * drafts everywhere. Absent on legacy docs (fallback: messageCount > 0).
   */
  hasUserContent?: boolean;
  /**
   * Sticky archive state. Present (false) on CREATE so the index query
   * (`where('archived','==',false)`) matches the doc; OMITTED on every routine
   * update so a `merge:true` upsert can never flip a server-side archive back
   * to false. Only archiveChatThread writes `true`.
   */
  archived?: boolean;
  /**
   * SERVER-OWNED shared-thread marker (live-membership surfacing, 2026-08-16):
   * stamped by the membership backend (Admin SDK) when this thread has other
   * registered participants — on the index doc it pushes into an ADDED
   * member's rail, and onto existing members' docs the moment a thread
   * becomes a group thread. The client PARSES it (group-overlay listener
   * gate, sidebar "group threads" section, membership-refresh trigger) and
   * must NEVER write it — same one-way discipline as `archived`/routine
   * fields (see the deliberate omission in toChatThreadDoc).
   */
  sharedThread?: boolean;
  /**
   * SERVER-OWNED thread KIND (2026-09-16): what sort of thread this is, when
   * it is a sort the rail gives a section of its own. Only ever
   * `'triage-report'` so far. Same one-way discipline as `sharedThread` — the
   * client PARSES it (its natural section) and must NEVER write it; see the
   * deliberate omission in toChatThreadDoc.
   */
  threadKind?: string;
  /**
   * SERVER-OWNED (2026-09-23): on the pair's direct message, the other
   * person's uid. Same one-way discipline as `threadKind` above — parsed,
   * never written (see toChatThreadDoc).
   */
  directWith?: string;
  /**
   * A per-thread bell, mirrored for the server push fan-out (2026-09-08).
   *
   * LEGACY SINCE 2026-09-17, kept deliberately: the setting is now ONE PER
   * PERSON (`chat_threads_meta/notifications`), and the client no longer writes
   * this. It survives as the ladder's second rung — a STARTING POSITION for
   * somebody who has never chosen, which is what keeps the 2026-09-16 founder
   * platform-thread defaults working and is why that change needed no backfill.
   */
  notifyMode?: 'chime' | 'chime-platform' | 'off';
  /**
   * SERVER-OWNED: when a message in this thread was last addressed TO the
   * viewer (2026-09-17) — they were named in it, it replied to them, or
   * Prest0n answered their own question.
   *
   * The rail watcher reads the index doc and never sees message text, so this
   * is how "was that arrival for me?" is answerable at all. Under the
   * mentions-only bell the chime fires on THIS field moving rather than on
   * `lastActiveAt`. Written by groupThreadDurableWrite's two push doors,
   * forward-only.
   */
  lastAddressedAt?: string;
  /**
   * SERVER-OWNED unseen counter for SHARED threads (read receipts, 2026-09-08
   * PR-3): the group durable-write path increments it on every member's index
   * doc except the actor's; the viewer's dwell (3 s on screen) zeroes it
   * through `clearThreadUnseen`. Parsed into `unreadCount` when the thread is
   * shared so every device shows the same count; the client NEVER writes it
   * from the thread object (same one-way discipline as `sharedThread`), and
   * firestore.rules lets a client write leave it unchanged or set it to 0 —
   * never raise it.
   */
  unseenCount?: number;
  /**
   * PERSONAL ORGANIZATION (PR-5, 2026-09-09): the viewer's category id this
   * thread is filed under. Client-written (the viewer's own doc); parsed
   * back only as a non-empty string.
   */
  category?: string;
  /** When `category` was last set, ms (2026-09-28) — the newer filing wins across devices. */
  categoryAt?: number;
  /**
   * NAME HISTORY (PR-5): explicit renames, oldest first, capped at 50 —
   * client-written for solo threads; the server writes it for shared threads
   * (the unified rename fans the title out to every member's doc).
   */
  titleHistory?: ThreadTitleHistoryEntry[];
  updatedAtTs: ReturnType<typeof ts.serverNow>;
  schemaVersion: 1;
  accountId?: string;
  sessionId?: string;
  /**
   * ROUTINE MIRROR (Routine Threads, 2026-07-31): SERVER-OWNED fields written
   * onto the index doc by the routines backend (Admin SDK) only. Presence of
   * `routineId` marks a routine thread; on routine cancel the server
   * FieldValue.delete()s every one of these, reverting the thread to a normal
   * thread with history intact. The client PARSES them (fromChatThreadDoc)
   * and must NEVER write them — see the deliberate omission in
   * toChatThreadDoc below (same one-way discipline as `archived`).
   */
  routineId?: string;
  routineName?: string;
  routineStatus?: 'active' | 'paused' | 'completed';
  routineScheduleSummary?: string;
  /** ISO of the next scheduled run; null while paused/unscheduled (and for
   * event-trigger routines, which have no clock). */
  routineNextRunAt?: string | null;
  routineLastRunAt?: string;
  routineLastRunStatus?: 'ok' | 'error';
  /** Routines v2 (2026-08-16): how many routines this thread holds. The
   * singular fields above describe the PRIMARY routine when > 1. */
  routineCount?: number;
  /** Every routine here is a repeating message (PR-8): a turn here is an ordinary turn. */
  routineMessageOnly?: boolean;
  /** Set when the server auto-created this routine thread from another thread. */
  routineHandoffFromThreadId?: string;
  routineHandoffAt?: string;
  /** ISO written by the CLIENT once it auto-navigated (acknowledgeRoutineHandoff). */
  routineHandoffAckAt?: string;
}

export function toChatThreadDoc(
  thread: ChatThread,
  instanceId: string,
  options?: {
    isCreate?: boolean;
    /**
     * Multi-org P2 (2026-08-16): the resolved active workspace at CREATE
     * time. Stamped once, together with the deterministic per-thread session
     * id (`sess_thr_{threadId}` — threadInvariant.ts) — the thread's
     * IMMUTABLE workspace binding (plan D3). NEVER written on updates:
     * merge writes preserve the stored value, and firestore.rules rejects
     * any client write that changes an existing accountId. Null/absent →
     * omitted; the server-side backfill (registry/session-derived) covers
     * threads created before resolution completed.
     */
    accountId?: string | null;
    /**
     * Write the thread's section (`category`, `categoryAt`) on an UPDATE.
     * A create always carries it. See the ONE WRITER note below.
     */
    withCategory?: boolean;
  },
): ChatThreadDoc {
  // Firestore rejects `undefined` field values (ignoreUndefinedProperties is
  // not enabled on this app — see src/utils/firestore.ts). Mirror the
  // conversationHistoryFirestore mappers and only include optional fields
  // when they are defined, so a freshly-created thread (no topicSummary /
  // userRenamed) writes cleanly via setDoc(merge:true).
  const doc: ChatThreadDoc = {
    id: thread.id,
    instanceId,
    title: thread.title,
    createdAt: thread.createdAt,
    lastActiveAt: thread.lastActiveAt,
    lastViewedAt: thread.lastViewedAt,
    unreadCount: thread.unreadCount,
    isProcessing: thread.isProcessing,
    messageCount: thread.messageCount,
    updatedAtTs: ts.serverNow(),
    schemaVersion: 1,
  };
  // BUG-THREAD-RESURRECTION (root fix, 2026-07-12): `archived` was stamped
  // `false` on EVERY upsert, so any routine title/count/lastActive update from
  // ANY window still holding the thread locally (another device, another tab,
  // an incognito session — none of which share the local tombstones) silently
  // un-archived it on the server, resurrecting the chip everywhere. Now:
  //   • CREATE  → archived:false (the field must EXIST for the index query
  //     `where('archived','==',false)` to match the doc);
  //   • UPDATE  → field omitted → setDoc(merge:true) preserves the server
  //     value, so an archived thread can never be un-archived by an upsert.
  // firestore.rules additionally rejects any archived true→false client write
  // (defense-in-depth against stale bundles that still stamp archived:false).
  if (options?.isCreate) {
    doc.archived = thread.archived === true;
    // Workspace binding — create-only, same one-way discipline as `archived`.
    const boundAccount = (options.accountId || '').trim();
    if (boundAccount) {
      doc.accountId = boundAccount;
      doc.sessionId = `sess_thr_${thread.id}`;
    }
  } else if (thread.archived === true) {
    doc.archived = true;
  }
  if (thread.topicSummary !== undefined) doc.topicSummary = thread.topicSummary;
  if (thread.userRenamed !== undefined) doc.userRenamed = thread.userRenamed;
  if (thread.pinned !== undefined) doc.pinned = thread.pinned;
  if (thread.pinnedAt !== undefined) doc.pinnedAt = thread.pinnedAt;
  if (thread.hasUserContent !== undefined) doc.hasUserContent = thread.hasUserContent;
  // Per-thread bell mirror (2026-09-08): the fan-out reads exactly this key.
  if (thread.notifyMode !== undefined) doc.notifyMode = thread.notifyMode;
  // PR-5 (2026-09-09): the viewer's own organization + name history. An
  // explicit empty string clears the category (setDoc(merge) needs a value
  // to overwrite the stored one); an absent field leaves it untouched.
  //
  // ONE WRITER (2026-09-29): an UPDATE leaves the section alone unless the
  // caller asks. A filing is written by its own direct write
  // (chatThreadIndexRepository.writeChatThreadCategory), so a whole-record
  // save from a stale copy (a queued save from before the filing, another tab)
  // can no longer file the thread back where that copy last saw it. A create
  // carries it: a thread filed before its record exists is filed by its birth.
  if (options?.isCreate || options?.withCategory) {
    if (thread.category !== undefined) doc.category = thread.category;
    if (typeof thread.categoryAt === 'number' && Number.isFinite(thread.categoryAt)) doc.categoryAt = thread.categoryAt;
  }
  if (thread.titleHistory !== undefined) doc.titleHistory = normalizeTitleHistory(thread.titleHistory);
  // ROUTINE MIRROR — DELIBERATELY OMITTED (Routine Threads, 2026-07-31).
  // The routine* fields are server-owned: the routines backend writes them via
  // Admin SDK and DELETES them all on cancel (FieldValue.delete()). A client
  // merge upsert that re-stamped a locally-cached copy would resurrect a
  // cancelled routine's mirror (the exact BUG-THREAD-RESURRECTION class the
  // `archived` handling above exists for), and a stale copy would clobber a
  // fresher nextRunAt/lastRunStatus written by the sweep. So, like `archived`
  // on updates: the client never includes them, and setDoc(merge:true)
  // preserves whatever the server last wrote.
  // `threadKind` — DELIBERATELY OMITTED (2026-09-16): server-owned, same
  //   one-way discipline as `sharedThread` directly below.
  // `directWith` — DELIBERATELY OMITTED (2026-09-23): server-owned beside
  //   `threadKind`; a stale local copy re-stamped by a merge upsert would call
  //   a thread that stopped being the pair's direct message one again.
  // `sharedThread` — DELIBERATELY OMITTED for the same reason (2026-08-16):
  // server-owned membership marker; client merge upserts preserve it.
  // `unseenCount` — DELIBERATELY OMITTED (PR-3, 2026-09-08): the server counts
  // a shared thread's unseen messages; the only client write is the dwell's
  // explicit zero (chatThreadIndexRepository.clearThreadUnseen).
  return doc;
}

/** Bounded, well-typed rename history (PR-5): malformed entries dropped, newest 50 kept. */
export function normalizeTitleHistory(raw: unknown): ThreadTitleHistoryEntry[] {
  const list = Array.isArray(raw) ? raw : [];
  const out: ThreadTitleHistoryEntry[] = [];
  for (const entry of list) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    const title = typeof e.title === 'string' ? e.title.trim().slice(0, 200) : '';
    const at = Number(e.at);
    if (!title || !Number.isFinite(at) || at <= 0) continue;
    const item: ThreadTitleHistoryEntry = { title, at: Math.floor(at) };
    if (typeof e.previousTitle === 'string' && e.previousTitle.trim()) item.previousTitle = e.previousTitle.trim().slice(0, 200);
    if (typeof e.byUid === 'string' && e.byUid.trim()) item.byUid = e.byUid.trim();
    if (typeof e.byName === 'string' && e.byName.trim()) item.byName = e.byName.trim().slice(0, 120);
    out.push(item);
  }
  return out.length > THREAD_TITLE_HISTORY_MAX ? out.slice(out.length - THREAD_TITLE_HISTORY_MAX) : out;
}

export function fromChatThreadDoc(doc: ChatThreadDoc): ChatThread {
  const thread: ChatThread = {
    id: doc.id,
    // THE RENAME HISTORY OUTRANKS A CLOBBERED TITLE (2026-09-11). A stored row
    // whose `userRenamed` is true and whose newest history entry names a
    // different title is a row whose name was overwritten without a rename —
    // the exact state the pre-fix merge left behind. Reading the history back
    // here is what returns the name on rows that are ALREADY in that state;
    // `carryLocalRename` is what stops new ones being made. For every healthy
    // row the two agree and this is the identity.
    title: authoritativeThreadTitle({ title: doc.title, userRenamed: doc.userRenamed, titleHistory: doc.titleHistory }),
    createdAt: doc.createdAt,
    lastActiveAt: doc.lastActiveAt,
    lastViewedAt: doc.lastViewedAt,
    unreadCount: doc.unreadCount,
    isProcessing: doc.isProcessing,
    messageCount: doc.messageCount,
    topicSummary: doc.topicSummary,
    userRenamed: doc.userRenamed,
    pinned: doc.pinned,
    pinnedAt: doc.pinnedAt,
    archived: doc.archived,
    hasUserContent: doc.hasUserContent,
  };
  // SERVER-OWNED shared-thread marker — parsed defensively like the routine
  // mirror: only a literal true carries through (a malformed value degrades
  // to "not marked", never to a wrongly-attached group listener).
  if (doc.sharedThread === true) thread.sharedThread = true;
  if (typeof doc.threadKind === 'string' && doc.threadKind) thread.threadKind = doc.threadKind;
  if (typeof doc.directWith === 'string' && doc.directWith.trim()) thread.directWith = doc.directWith.trim();
  // CONVERGED UNREAD (PR-3, 2026-09-08): a shared thread's count is the
  // server's `unseenCount` — bumped for every member but the actor, zeroed by
  // the viewer's dwell — so all of a viewer's devices agree. A doc the server
  // has not stamped yet keeps the client counter; a malformed value is ignored.
  if (thread.sharedThread === true) {
    const raw = (doc as unknown as Record<string, unknown>).unseenCount;
    if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 0) {
      thread.unreadCount = Math.floor(raw);
    }
  }
  // Per-thread bell mirror (2026-09-08; the ladder's starting-position rung
  // since 2026-09-17) — only a known mode carries through.
  if (doc.notifyMode === 'chime' || doc.notifyMode === 'chime-platform' || doc.notifyMode === 'off') {
    thread.notifyMode = doc.notifyMode;
  }
  // The rail's "this arrival was for you" fact (2026-09-17), server-owned.
  {
    const addressed = (doc as unknown as Record<string, unknown>).lastAddressedAt;
    if (typeof addressed === 'string' && addressed.trim()) thread.lastAddressedAt = addressed;
  }
  // PR-5 (2026-09-09): personal category (non-empty string only) + name history.
  if (typeof doc.category === 'string' && doc.category.trim()) thread.category = doc.category.trim();
  {
    const at = Number((doc as unknown as Record<string, unknown>).categoryAt);
    if (Number.isFinite(at) && at > 0) thread.categoryAt = at;
  }
  {
    const history = normalizeTitleHistory((doc as unknown as Record<string, unknown>).titleHistory);
    if (history.length > 0) thread.titleHistory = history;
  }
  // WHO caused the latest server-side activity bump (2026-08-27): the
  // notification watcher stays quiet when it was the viewer themself —
  // including their sends from OTHER devices, which no per-tab window covers.
  {
    const raw = (doc as unknown as Record<string, unknown>).lastActivityBy;
    if (typeof raw === 'string') thread.lastActivityBy = raw.trim() || undefined;
  }
  // MULTI-ORG P4: workspace binding — parse-only (a malformed value degrades
  // to "unbound", i.e. visible everywhere, never a hidden thread).
  if (typeof doc.accountId === 'string' && doc.accountId.trim()) {
    thread.accountId = doc.accountId.trim();
  }
  // ROUTINE MIRROR — defensive parse (server-owned fields; a malformed value
  // from a partial Admin write or future schema drift must degrade to "not a
  // routine thread"/"field absent", never crash the index subscription).
  // Fields are only assigned when well-typed, so a non-routine thread carries
  // NO routine keys at all (clean spreads, clean JSON persistence).
  if (typeof doc.routineId === 'string' && doc.routineId) {
    thread.routineId = doc.routineId;
    if (typeof doc.routineName === 'string') thread.routineName = doc.routineName;
    if (doc.routineStatus === 'active' || doc.routineStatus === 'paused' || doc.routineStatus === 'completed') {
      thread.routineStatus = doc.routineStatus;
    }
    if (typeof doc.routineScheduleSummary === 'string') {
      thread.routineScheduleSummary = doc.routineScheduleSummary;
    }
    if (typeof doc.routineNextRunAt === 'string' || doc.routineNextRunAt === null) {
      thread.routineNextRunAt = doc.routineNextRunAt;
    }
    if (typeof doc.routineLastRunAt === 'string') thread.routineLastRunAt = doc.routineLastRunAt;
    if (doc.routineLastRunStatus === 'ok' || doc.routineLastRunStatus === 'error') {
      thread.routineLastRunStatus = doc.routineLastRunStatus;
    }
    // v2 multi-routine count: only a finite positive number carries through
    // (absent on pre-v2 mirrors — renderers treat absence as 1).
    if (typeof doc.routineCount === 'number' && Number.isFinite(doc.routineCount) && doc.routineCount > 0) {
      thread.routineCount = Math.floor(doc.routineCount);
    }
    // Only a literal true: an older server never writes it, and absence is an ordinary routine thread.
    if (doc.routineMessageOnly === true) thread.routineMessageOnly = true;
  }
  // Handoff fields are parsed INDEPENDENTLY of routineId: the auto-navigation
  // effect must still fire if the routine-doc write and the handoff stamp ever
  // land in separate snapshots.
  if (typeof doc.routineHandoffFromThreadId === 'string' && doc.routineHandoffFromThreadId) {
    thread.routineHandoffFromThreadId = doc.routineHandoffFromThreadId;
  }
  if (typeof doc.routineHandoffAt === 'string') thread.routineHandoffAt = doc.routineHandoffAt;
  if (typeof doc.routineHandoffAckAt === 'string') thread.routineHandoffAckAt = doc.routineHandoffAckAt;
  return thread;
}
